/**
 * Incoming Transactions - the staging step before the ledger.
 *
 * One component serves two modules: the admin one (`/admin/bank-incoming`,
 * every fund side by side) and the fund one (a fund's General Ledger → Incoming
 * Transactions). Passing `fundId` swaps both the endpoints it reads and the
 * chrome: the fund picker disappears and the fund column becomes the account.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  ArrowLeftRight,
  Check,
  Inbox,
  RefreshCw,
  Search,
  Sparkles,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  SuggestionActions,
  SuggestionBadge,
  SuggestionText,
} from '@/components/general-ledger/SuggestionCell';
import {
  IncomingLineAllocations,
  IncomingLineAuditTrail,
  IncomingLineDetails,
} from '@/components/bank-incoming/IncomingLineDetails';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MatchManuallyView } from '@/components/bank-incoming/MatchManuallyView';
import { RecordConversionFromLinesPanel } from '@/components/bank-incoming/RecordConversionFromLinesPanel';
import { RefreshSuggestionsButton } from '@/components/bank-incoming/RefreshSuggestionsButton';
import { couldBeConversion } from '@/components/bank-incoming/conversionFromBankLinesValidators';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useBankIncomingLine,
  useBankIncomingList,
  useBankIncomingSummary,
  useBulkAcceptBankIncoming,
} from '@/hooks/react-query/useBankIncoming';
import { useFunds } from '@/hooks/react-query/useFunds';
import type {
  BankIncomingFilters,
  BankIncomingScope,
} from '@/services/bankIncomingService';
import {
  BANK_TRANSACTION_TYPE_LABELS,
  INCOMING_MATCH_STATUS_LABELS,
  MATCH_BASIS_LABELS,
  type BankIncomingAdminLine,
  type BankTransactionType,
  type IncomingMatchStatus,
} from '@/types/bank-reconciliation';
import { formatCurrency, formatDate } from '@/utils/formatters';

const ALL = 'all';
const DEFAULT_PAGE_SIZE = 25;
// Which line the Match Manually screen is working on, carried in the URL.
const MATCH_PARAM = 'match';

const STATUS_OPTIONS: { value: IncomingMatchStatus; label: string }[] = [
  { value: 'UNMATCHED', label: 'Awaiting match' },
  { value: 'PARTIAL_MATCHED', label: 'Partly applied' },
  { value: 'MATCHED', label: 'Matched' },
  { value: 'INTERNAL_TRANSFER', label: 'Internal transfer' },
  { value: 'IGNORED', label: 'Ignored' },
];

const TYPE_OPTIONS = Object.entries(BANK_TRANSACTION_TYPE_LABELS) as [
  BankTransactionType,
  string,
][];

function StatTile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold text-foreground">{value}</p>
    </div>
  );
}

function formatBacklog(amountByCurrency: Record<string, number>): string {
  return Object.entries(amountByCurrency)
    .map(([currency, amount]) =>
      formatCurrency(amount, {
        currency: currency === '-' ? undefined : currency,
      })
    )
    .join(' · ');
}

function LinesPlaceholder({ isLoading }: { isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        Loading incoming transactions...
      </div>
    );
  }
  return (
    <div className="py-10 text-center text-muted-foreground">
      No incoming transactions for this selection.
    </div>
  );
}

export interface IncomingTransactionsViewProps {
  /** Narrows the view to one fund and to that fund's own endpoints. */
  fundId?: string;
  /** The admin page owns the page title; the fund tab sits under one already. */
  showHeading?: boolean;
}

// Only unmatched lines carrying a suggestion can be accepted in bulk.
const isAcceptable = (line: BankIncomingAdminLine) =>
  line.match_status === 'UNMATCHED' && !!line.suggested_fund_transaction_id;

export function IncomingTransactionsView({
  fundId,
  showHeading = true,
}: IncomingTransactionsViewProps) {
  const scope: BankIncomingScope | undefined = useMemo(
    () => (fundId ? { fundId } : undefined),
    [fundId]
  );
  const navigate = useNavigate();

  const [selectedFundId, setSelectedFundId] = useState(ALL);
  const [status, setStatus] = useState<string>('UNMATCHED');
  const [type, setType] = useState(ALL);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selected, setSelected] = useState<BankIncomingAdminLine | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const matchingId = searchParams.get(MATCH_PARAM);
  const [suggestedOnly, setSuggestedOnly] = useState(false);
  // Lines, not ids: a conversion's legs and hops can sit on different pages.
  const [checked, setChecked] = useState<Map<string, BankIncomingAdminLine>>(
    () => new Map()
  );
  const [recordingConversion, setRecordingConversion] = useState(false);
  const bulkAccept = useBulkAcceptBankIncoming(scope);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  // Under a fund scope the path already carries the fund - sending it again as
  // a filter would only be a second way to say the same thing.
  const fundFilter =
    scope || selectedFundId === ALL ? undefined : selectedFundId;

  // The summary reports the whole backlog for the fund/date/search selection,
  // so it stays comparable while status and type narrow the table.
  const scopeFilters: BankIncomingFilters = {
    fund_id: fundFilter,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    search: search || undefined,
  };
  const filters: BankIncomingFilters = {
    ...scopeFilters,
    match_status: status === ALL ? undefined : (status as IncomingMatchStatus),
    transaction_type: type === ALL ? undefined : (type as BankTransactionType),
    suggested_only: suggestedOnly || undefined,
    page,
    page_size: pageSize,
  };

  const { data, isLoading } = useBankIncomingList(filters, scope);
  const { data: summary } = useBankIncomingSummary(scopeFilters, scope);

  const lines = data?.items ?? [];
  const total = data?.total_count ?? 0;

  // Under a fund, any unmatched line can also be picked into a conversion.
  const isSelectable = (line: BankIncomingAdminLine) =>
    isAcceptable(line) || (!!fundId && line.match_status === 'UNMATCHED');
  const acceptable = lines.filter(isAcceptable);
  const checkedLines = [...checked.values()];
  const checkedAcceptableIds = checkedLines
    .filter(isAcceptable)
    .map((line) => line.id);
  const canRecordConversion = !!fundId && couldBeConversion(checkedLines);
  const toggle = (line: BankIncomingAdminLine) =>
    setChecked((current) => {
      const next = new Map(current);
      if (next.has(line.id)) next.delete(line.id);
      else next.set(line.id, line);
      return next;
    });
  const clearChecked = () => {
    setChecked(new Map());
    setRecordingConversion(false);
  };

  const resetToFirstPage = <T,>(setter: (value: T) => void) => {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  };

  const backlog = summary
    ? formatBacklog(summary.unmatched_amount_by_currency)
    : '-';

  const openMatch = (incomingId: string) => {
    setSelected(null);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(MATCH_PARAM, incomingId);
        return next;
      },
      { replace: false }
    );
  };

  const closeMatch = () =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(MATCH_PARAM);
        return next;
      },
      { replace: false }
    );

  if (matchingId) {
    return (
      <MatchManuallyScreen
        incomingId={matchingId}
        scope={scope}
        onClose={closeMatch}
      />
    );
  }

  return (
    <div className="space-y-6">
      {showHeading && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Inbox className="h-6 w-6" />
              Incoming Transactions
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Bank lines delivered by the provider that no fund transaction has
              claimed yet. Staging only - these carry no GL impact until they
              are matched.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        <StatTile
          label="Awaiting match"
          value={summary?.unmatched_count ?? '-'}
        />
        <StatTile
          label="Partly applied"
          value={summary?.partly_applied_count ?? '-'}
        />
        <StatTile label="Matched" value={summary?.matched_count ?? '-'} />
        <StatTile label="Unapplied net" value={backlog} />
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Counterparty, reference, description..."
                className="pl-9"
              />
            </div>

            {!scope && (
              <FundFilterSelect
                value={selectedFundId}
                onChange={resetToFirstPage(setSelectedFundId)}
              />
            )}

            <Select value={status} onValueChange={resetToFirstPage(setStatus)}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={type} onValueChange={resetToFirstPage(setType)}>
              <SelectTrigger className="w-[190px]">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {TYPE_OPTIONS.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => resetToFirstPage(setDateFrom)(e.target.value)}
              className="w-[150px]"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => resetToFirstPage(setDateTo)(e.target.value)}
              className="w-[150px]"
            />

            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={suggestedOnly}
                onCheckedChange={(value) => {
                  setSuggestedOnly(value === true);
                  setPage(1);
                }}
              />
              With a suggestion
            </label>

            {scope && <RefreshSuggestionsButton scope={scope} />}
          </div>

          {checked.size > 0 && (
            <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-4 py-2">
              <span className="text-sm text-foreground">
                {checked.size} line{checked.size === 1 ? '' : 's'} selected
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={clearChecked}>
                  Clear
                </Button>
                {fundId && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canRecordConversion || recordingConversion}
                    onClick={() => setRecordingConversion(true)}
                  >
                    <ArrowLeftRight className="h-3.5 w-3.5 mr-1" />
                    Record as currency conversion
                  </Button>
                )}
                <Button
                  size="sm"
                  disabled={
                    bulkAccept.isPending || checkedAcceptableIds.length === 0
                  }
                  onClick={() =>
                    // Lines the endpoint could not accept stay selected, so a
                    // retry doesn't mean re-picking them out of the list.
                    bulkAccept.mutate(checkedAcceptableIds, {
                      onSuccess: (result) =>
                        setChecked(
                          (current) =>
                            new Map(
                              [...current].filter(
                                ([id, line]) =>
                                  id in result.failed || !isAcceptable(line)
                              )
                            )
                        ),
                    })
                  }
                >
                  <Check className="h-3.5 w-3.5 mr-1" />
                  Accept selected
                </Button>
              </div>
            </div>
          )}

          {fundId && recordingConversion && (
            <RecordConversionFromLinesPanel
              // Remounted per selection, so the prefilled pairing follows it.
              key={[...checked.keys()].sort().join()}
              fundId={fundId}
              lines={checkedLines}
              onRecorded={clearChecked}
              onCancel={() => setRecordingConversion(false)}
            />
          )}

          {isLoading || lines.length === 0 ? (
            <LinesPlaceholder isLoading={isLoading} />
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[44px]">
                      <Checkbox
                        checked={
                          acceptable.length > 0 &&
                          acceptable.every((line) => checked.has(line.id))
                        }
                        disabled={acceptable.length === 0}
                        onCheckedChange={(value) =>
                          // This page only: picks on other pages stay selected.
                          setChecked((current) => {
                            const next = new Map(current);
                            for (const line of acceptable) {
                              if (value === true) next.set(line.id, line);
                              else next.delete(line.id);
                            }
                            return next;
                          })
                        }
                      />
                    </TableHead>
                    <TableHead className="w-[110px]">DATE</TableHead>
                    <TableHead className="w-[150px]">
                      {scope ? 'ACCOUNT' : 'FUND'}
                    </TableHead>
                    <TableHead className="w-[170px]">COUNTERPARTY</TableHead>
                    <TableHead className="w-[170px]">TYPE</TableHead>
                    <TableHead className="text-right w-[140px]">
                      AMOUNT
                    </TableHead>
                    <TableHead>SUGGESTION</TableHead>
                    <TableHead className="w-[150px]">STATUS</TableHead>
                    <TableHead className="w-[160px]">ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow
                      key={line.id}
                      className="cursor-pointer"
                      onClick={() => setSelected(line)}
                    >
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={checked.has(line.id)}
                          disabled={!isSelectable(line)}
                          onCheckedChange={() => toggle(line)}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(line.value_date ?? line.execution_date)}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">
                          {scope ? line.account_name : line.fund_name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {scope ? line.bank_name : line.account_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        {line.counterparty_name ||
                          line.counterparty_reference ||
                          '-'}
                      </TableCell>
                      <TableCell>
                        <TypeCell line={line} />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        <span
                          className={
                            line.amount < 0 ? 'text-red-600' : 'text-green-700'
                          }
                        >
                          {formatCurrency(line.amount, {
                            currency: line.currency ?? undefined,
                          })}
                        </span>
                        {line.match_status === 'PARTIAL_MATCHED' && (
                          <p className="text-xs text-amber-600">
                            {formatCurrency(line.unapplied_amount, {
                              currency: line.currency ?? undefined,
                            })}{' '}
                            left
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[420px]">
                        <div className="space-y-1">
                          <SuggestionBadge line={line} />
                          <SuggestionText line={line} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusCell line={line} />
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <SuggestionActions
                          line={line}
                          scope={scope}
                          onAllocate={() => openMatch(line.id)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {total > pageSize && (
            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={[25, 50, 100]}
            />
          )}
        </CardContent>
      </Card>

      {selected && (
        <IncomingDetailDialog
          line={selected}
          scope={scope}
          onClose={() => setSelected(null)}
          onMatchManually={() => openMatch(selected.id)}
          onDesignateFundCost={() =>
            navigate(
              `/app/funds/${selected.fund_id}/fund-costs/designate/${selected.id}`
            )
          }
        />
      )}
    </div>
  );
}

function MatchManuallyScreen({
  incomingId,
  scope,
  onClose,
}: {
  incomingId: string;
  scope?: BankIncomingScope;
  onClose: () => void;
}) {
  const { data: line, isLoading } = useBankIncomingLine(incomingId, scope);

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">Loading transaction…</p>
    );
  }
  if (!line) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          That bank transaction no longer exists.
        </p>
        <Button variant="outline" onClick={onClose}>
          Back to Incoming Transactions
        </Button>
      </div>
    );
  }
  return <MatchManuallyView line={line} scope={scope} onClose={onClose} />;
}

// Its own component so the fund list is only fetched by the admin module - the
// fund module never renders it and so never asks for every fund.
function FundFilterSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { data: funds } = useFunds();

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[200px]">
        <SelectValue placeholder="All funds" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All funds</SelectItem>
        {(funds ?? []).map((fund) => (
          <SelectItem key={fund.id} value={fund.id}>
            {fund.display_name || fund.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Before matching, the bank's own classification is all a line knows; once
// matched, the internal fund transaction type takes over.
function TypeCell({ line }: { line: BankIncomingAdminLine }) {
  if (line.fund_transaction_type) {
    return <Badge variant="secondary">{line.fund_transaction_type}</Badge>;
  }
  return (
    <span className="text-sm text-muted-foreground">
      {BANK_TRANSACTION_TYPE_LABELS[line.bank_transaction_type]}
    </span>
  );
}

// An auto-applied line is one the batch booked on its own: tagged so a reviewer
// can find every unattended booking.
function StatusCell({ line }: { line: BankIncomingAdminLine }) {
  if (line.match_status === 'MATCHED') {
    const auto =
      line.suggestion_basis === null &&
      line.fund_bank_account_transaction_id !== null;
    return (
      <div className="space-y-1">
        <Badge variant="secondary">Matched</Badge>
        {auto && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            automatic
          </div>
        )}
      </div>
    );
  }
  if (line.match_status === 'UNMATCHED') {
    return (
      <Badge variant={line.suggestion_basis ? 'outline' : 'warning'}>
        {line.suggestion_basis ? 'Suggested' : 'Awaiting match'}
      </Badge>
    );
  }
  if (line.match_status === 'PARTIAL_MATCHED') {
    return <Badge variant="warning">Partly applied</Badge>;
  }
  return (
    <Badge variant="secondary">
      {INCOMING_MATCH_STATUS_LABELS[line.match_status]}
    </Badge>
  );
}

function IncomingDetailDialog({
  line,
  scope,
  onClose,
  onMatchManually,
  onDesignateFundCost,
}: {
  line: BankIncomingAdminLine;
  scope?: BankIncomingScope;
  onClose: () => void;
  onMatchManually: () => void;
  onDesignateFundCost: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader className="flex-none pr-8">
          <DialogTitle>
            {formatCurrency(line.amount, {
              currency: line.currency ?? undefined,
            })}
            {line.counterparty_name ? ` - ${line.counterparty_name}` : ''}
          </DialogTitle>
          <DialogDescription>
            Staged bank line as delivered by {line.source}. Not part of the
            general ledger until it is matched to a fund transaction.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <IncomingLineDetails line={line} />

          <IncomingLineAllocations line={line} scope={scope} />

          {line.suggestion_note && (
            <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">
                  What the matcher found
                </span>
                {line.suggestion_basis && (
                  <Badge variant="outline">
                    {MATCH_BASIS_LABELS[line.suggestion_basis]}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-foreground">{line.suggestion_note}</p>
              {line.suggestion_alternatives.length > 0 && (
                <div className="pt-2 space-y-1 border-t border-border">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Other candidates
                  </p>
                  {line.suggestion_alternatives.map((alternative) => (
                    <p
                      key={`${alternative.basis}-${alternative.fund_transaction_id ?? ''}`}
                      className="text-sm text-muted-foreground"
                    >
                      {alternative.explanation}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          <IncomingLineAuditTrail incomingId={line.id} scope={scope} />
        </DialogBody>

        {/* Pinned: the decision is the point of the dialog, so it must not be
            somewhere the operator has to scroll to find. */}
        {(line.match_status === 'UNMATCHED' ||
          line.match_status === 'PARTIAL_MATCHED') && (
          <DialogFooter className="flex-none border-t border-border pt-4">
            {line.match_status === 'UNMATCHED' && line.amount < 0 && (
              <Button variant="outline" onClick={onDesignateFundCost}>
                Designate as fund cost
              </Button>
            )}
            {line.suggestion_basis ? (
              <SuggestionActions
                line={line}
                scope={scope}
                onAllocate={onMatchManually}
              />
            ) : (
              <Button variant="outline" onClick={onMatchManually}>
                Allocate
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
